var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { GPTLoader } from './gpt-loader';
import { AdEventType } from '../types';
/**
 * Host-rendered display interstitial — fullscreen overlay containing an
 * in-page GPT slot sized to the AY bid (or a 300×250 default).
 *
 * Why not `OutOfPageFormat.INTERSTITIAL`: GPT's web-interstitial format
 * is "Google-trigger-based" — the publisher requests it via `display()`,
 * but Google decides when to show it (on the next page navigation, after
 * heuristics determine it's an appropriate moment, etc.). For an
 * on-demand SDK trigger (`showInterstitial` from the game), this means
 * the ad never paints in the current session — exactly the bug reported.
 * Calling `display()` on the OutOfPage slot was a no-op for our use case.
 *
 * Replacement: render our own overlay (same pattern the IMA video
 * interstitial uses) containing a regular in-page GPT slot. The slot is
 * defined with the same multi-size list the AY bid request sent, so any
 * AY-eligible creative or direct GAM line item can serve. After
 * `slotRenderEnded`, the rendered iframe + its `__container__` wrapper
 * are force-resized to the bid size — same Prebid Universal Creative
 * SafeFrame workaround as the banner renderer (PUC paints at 1×1 and
 * doesn't self-resize through the SafeFrame).
 *
 * Promise resolution semantics:
 *  - Resolves when the user closes the ad via the X (or `destroy()` is
 *    called externally). Lets the game-side `await showInterstitial()`
 *    pause gameplay for the duration of the ad and resume on close.
 *  - Rejects with `Error('No ad available')` on a true no-fill — the
 *    host shell maps that exact message to `outcome: 'no_ad'`.
 *  - Rejects with any other error on a GPT failure.
 */
/**
 * Canonical interstitial sizes — kept in sync with
 * `src/bidding/openrtb-builder.ts` `INTERSTITIAL_BANNER_SIZES`. The GPT
 * slot's multi-size MUST match the bid request so AY's prebid line items
 * (which target specific sizes) can resolve a matching creative.
 */
const INTERSTITIAL_SLOT_SIZES = [
    [300, 250], [336, 280], [320, 480],
    [728, 90], [300, 600], [160, 600], [970, 250],
    [320, 50], [320, 100], [300, 50], [300, 75], [300, 100], [250, 250],
];
const DEFAULT_INTERSTITIAL_SIZE = [300, 250];
const SLOT_DIV_ID = 'getjar-interstitial-slot';
const OVERLAY_ID = 'getjar-interstitial-overlay';
/**
 * Forced-view countdown before the close X becomes tappable. Overridable
 * per-show via `InterstitialConfig.closeDelayMs`. Set to a value the SDK
 * shell can rely on across all GPT display interstitials — partner games
 * shouldn't need to know about it, but it must be long enough that the
 * creative actually paints before the user can dismiss.
 */
export const DEFAULT_CLOSE_DELAY_MS = 5000;
export class GPTInterstitialAds {
    constructor(eventEmitter, adUnit) {
        this.eventEmitter = eventEmitter;
        this.adUnit = adUnit;
        this.currentSlot = null;
        this.overlay = null;
        this.closeBtn = null;
        this.progressBar = null;
        this.closeDelayTimer = null;
        /**
         * Window-level resize/orientationchange handler that re-scales the slot
         * to fit when the viewport gets smaller than the rendered creative
         * (mobile portrait→landscape rotation, browser URL-bar show/hide,
         * etc.). Tracked so teardown can unsubscribe and we don't leak handlers
         * across shows.
         */
        this.viewportResizeHandler = null;
        this.resolvePromise = null;
        this.rejectPromise = null;
    }
    /**
     * Show an interstitial ad. Resolves on close, rejects on no-fill / error.
     */
    show(config, targeting) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (this.currentSlot || this.overlay) {
                throw new Error('Interstitial ad already in progress');
            }
            yield GPTLoader.load();
            const placement = (_a = config.placement) !== null && _a !== void 0 ? _a : config.placementName;
            yield this.eventEmitter.emit(AdEventType.AD_REQUESTED, {
                placement,
                adType: 'interstitial',
            });
            const [w, h] = resolveInterstitialSize(targeting);
            return new Promise((resolve, reject) => {
                this.resolvePromise = resolve;
                this.rejectPromise = reject;
                try {
                    // Mount the overlay BEFORE pushing the cmd queue so `display()`
                    // has a target div to attach to.
                    const closeDelayMs = resolveCloseDelayMs(config.closeDelayMs);
                    this.createOverlay(w, h, () => this.handleCloseClick(config), closeDelayMs);
                    window.googletag.cmd.push(() => {
                        try {
                            console.log(`[GetJarAds] Defining interstitial slot adUnit="${this.adUnit}" size=${w}x${h}`);
                            const slot = window.googletag.defineSlot(this.adUnit, INTERSTITIAL_SLOT_SIZES, SLOT_DIV_ID);
                            if (!slot) {
                                // Only fires when the adUnit is empty or the SLOT_DIV_ID is
                                // missing — both are programmer/config errors. The OutOfPage
                                // null-return that the prior implementation guarded for is
                                // no longer a possible path because we don't use OutOfPage.
                                const err = new Error(`[GetJarAds] Failed to define interstitial slot — adUnit="${this.adUnit || '(empty)'}". ` +
                                    `Check /ads/init adUnits.interstitial.`);
                                this.failWith(config, err);
                                return;
                            }
                            for (const [key, value] of Object.entries(targeting)) {
                                slot.setTargeting(key, value);
                            }
                            slot.addService(window.googletag.pubads());
                            window.googletag
                                .pubads()
                                .addEventListener('slotRenderEnded', (event) => {
                                if (event.slot !== slot)
                                    return;
                                this.handleRenderEnded(event, config, w, h);
                            });
                            console.log(`[GetJarAds] Calling googletag.display('${SLOT_DIV_ID}') and refresh([slot])...`);
                            window.googletag.display(SLOT_DIV_ID);
                            window.googletag.pubads().refresh([slot]);
                            this.currentSlot = slot;
                        }
                        catch (err) {
                            this.failWith(config, err instanceof Error ? err : new Error(String(err)));
                        }
                    });
                }
                catch (err) {
                    this.failWith(config, err instanceof Error ? err : new Error(String(err)));
                }
            });
        });
    }
    /**
     * Tear the overlay down. Idempotent. Emits AD_CLOSED + invokes
     * `config.onClose` on the first invocation only (subsequent calls are
     * no-ops once `currentSlot`/`overlay` are nulled out).
     */
    destroy() {
        if (!this.currentSlot && !this.overlay)
            return;
        this.teardown();
        this.eventEmitter.emit(AdEventType.AD_CLOSED, {
            placement: 'interstitial',
            adType: 'interstitial',
        });
    }
    // ---- internals ---------------------------------------------------------
    handleRenderEnded(event, config, w, h) {
        var _a, _b;
        console.log('[GetJarAds] Interstitial slotRenderEnded:', {
            isEmpty: event.isEmpty,
            size: event.size,
            creativeId: event.creativeId,
            lineItemId: event.lineItemId,
            advertiserId: event.advertiserId,
        });
        // `isEmpty: true` can fire even on a successful render during GPT's
        // own cleanup pass. The reliable "true no-fill" signal is
        // `isEmpty && no creative/lineItem/advertiser IDs`. This matches the
        // disposition logic we already used in the previous implementation.
        const hasAdIds = event.creativeId || event.lineItemId || event.advertiserId;
        if (event.isEmpty && !hasAdIds) {
            console.warn(`[GetJarAds] No interstitial ad available for ${(_a = config.placement) !== null && _a !== void 0 ? _a : config.placementName}`);
            this.failWith(config, new Error('No ad available'));
            return;
        }
        // Filled: force-resize the rendered iframe + its __container__ to the
        // resolved size. Same PUC SafeFrame workaround as the banner fix.
        forceResizeInterstitial(w, h);
        this.eventEmitter.emit(AdEventType.AD_LOADED, {
            placement: (_b = config.placement) !== null && _b !== void 0 ? _b : config.placementName,
            adType: 'interstitial',
        });
        // We do NOT resolve here. The Promise resolves when the user clicks
        // the close X (or `destroy()` is called externally) — see
        // `handleCloseClick`. Resolving on render would end the host's
        // `await renderer.show()` immediately and the bridge would report
        // `outcome: 'shown'` before the user has actually closed the ad.
    }
    handleCloseClick(config) {
        var _a;
        const resolve = this.resolvePromise;
        this.teardown();
        this.eventEmitter.emit(AdEventType.AD_CLOSED, {
            placement: (_a = config.placement) !== null && _a !== void 0 ? _a : config.placementName,
            adType: 'interstitial',
        });
        if (config.onClose) {
            try {
                config.onClose();
            }
            catch (err) {
                console.warn('[GetJarAds] onClose callback threw:', err);
            }
        }
        if (resolve)
            resolve();
    }
    /**
     * Emit AD_ERROR, run the onError callback, tear the overlay down, and
     * reject the show() Promise. Idempotent — guarded by `rejectPromise`
     * being nulled after the first call.
     */
    failWith(config, err) {
        var _a;
        const reject = this.rejectPromise;
        // Belt-and-braces: reveal the X before tearing down so any code that
        // observes the overlay between fail() and DOM removal sees a closable
        // ad. teardown() removes the overlay outright, so this is invisible
        // in the normal path — it's protection against future changes that
        // might keep the overlay up after a render failure.
        this.revealCloseButton();
        this.teardown();
        this.eventEmitter.emit(AdEventType.AD_ERROR, {
            placement: (_a = config.placement) !== null && _a !== void 0 ? _a : config.placementName,
            adType: 'interstitial',
            error: err.message,
        });
        if (config.onError) {
            try {
                config.onError(err);
            }
            catch (cbErr) {
                console.warn('[GetJarAds] onError callback threw:', cbErr);
            }
        }
        if (reject)
            reject(err);
    }
    teardown() {
        this.resolvePromise = null;
        this.rejectPromise = null;
        if (this.closeDelayTimer !== null) {
            clearTimeout(this.closeDelayTimer);
            this.closeDelayTimer = null;
        }
        if (this.viewportResizeHandler) {
            window.removeEventListener('resize', this.viewportResizeHandler);
            window.removeEventListener('orientationchange', this.viewportResizeHandler);
            if (typeof window.visualViewport !== 'undefined' && window.visualViewport) {
                window.visualViewport.removeEventListener('resize', this.viewportResizeHandler);
            }
            this.viewportResizeHandler = null;
        }
        if (this.currentSlot) {
            const slot = this.currentSlot;
            window.googletag.cmd.push(() => {
                try {
                    window.googletag.destroySlots([slot]);
                }
                catch (e) {
                    console.warn('[GetJarAds] destroySlots threw:', e);
                }
            });
            this.currentSlot = null;
        }
        if (this.overlay && this.overlay.parentNode) {
            this.overlay.parentNode.removeChild(this.overlay);
        }
        this.overlay = null;
        this.closeBtn = null;
        this.progressBar = null;
    }
    createOverlay(w, h, onCloseClick, closeDelayMs) {
        // Defensive: a previous show left an overlay behind (error path that
        // didn't reach teardown). Remove it before mounting the new one.
        const stale = document.getElementById(OVERLAY_ID);
        if (stale && stale.parentNode)
            stale.parentNode.removeChild(stale);
        this.overlay = document.createElement('div');
        this.overlay.id = OVERLAY_ID;
        // Mobile-correct viewport sizing:
        //  - `100vh` is the "large viewport height" on iOS Safari — it INCLUDES
        //    the URL bar's space, so when the URL bar is visible the bottom
        //    of the overlay sits off-screen and the close X is unreachable.
        //  - `100dvh` is the "dynamic viewport height" — tracks the URL bar
        //    in real time. Same story for `100dvw`. We list `100vh`/`100vw`
        //    FIRST as the fallback and `100dvh`/`100dvw` SECOND so a browser
        //    that doesn't understand `dvh` falls back gracefully (the second
        //    declaration wins on supporting browsers; the first survives on
        //    older ones).
        this.overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      width: 100dvw;
      height: 100vh;
      height: 100dvh;
      background: rgba(0, 0, 0, 0.85);
      z-index: 999999;
      display: flex;
      align-items: center;
      justify-content: center;
    `;
        // Slot container — sized to the resolved bid size, centered by the
        // overlay's flex layout. GPT injects its `__container__` div + iframe
        // inside this. We pre-size it so the layout doesn't shift when the
        // iframe arrives, and `forceResizeInterstitial` keeps it locked.
        //
        // `transform-origin: center` lets `fitSlotToViewport` shrink an
        // oversized creative (e.g. a 728×90 leaderboard or 320×480 portrait
        // interstitial that doesn't fit a landscape phone) via `transform:
        // scale(<n>)` while keeping it centered. The iframe's intrinsic
        // dimensions stay at w×h so the creative renders crisp — only the
        // visual presentation is scaled.
        const slotDiv = document.createElement('div');
        slotDiv.id = SLOT_DIV_ID;
        slotDiv.style.cssText = `
      position: relative;
      width: ${w}px;
      height: ${h}px;
      background: #000;
      transform-origin: center center;
    `;
        // Forced-view countdown bar — spans the top of the overlay (not the
        // slot, so it's always visible regardless of creative size), depletes
        // from full to empty over `closeDelayMs`, then is hidden. Decorative:
        // the spec is already conveyed by the X appearing afterwards.
        //
        // `top: env(safe-area-inset-top, 0)` keeps the bar below the notch
        // on iPhone X-class devices when the page is full-bleed under the
        // status bar; the fallback `0` covers everything else.
        const progressBar = document.createElement('div');
        progressBar.setAttribute('aria-hidden', 'true');
        progressBar.style.cssText = `
      position: absolute;
      top: env(safe-area-inset-top, 0);
      left: 0;
      width: 100%;
      height: 4px;
      background: #26C281;
      transform-origin: left center;
      transform: scaleX(1);
      z-index: 1000001;
      pointer-events: none;
    `;
        this.progressBar = progressBar;
        // Close X — top-right of the overlay (NOT inside the slot, so it
        // doesn't intercept clicks meant for the creative's click-through).
        // Hidden until the countdown elapses; the click handler is wired up
        // unconditionally so it works the instant we reveal the button.
        //
        // Position uses `max(16px, env(safe-area-inset-*))` so the X stays
        // 16px from the edge on devices without insets, and shifts inward
        // by the notch/home-indicator amount on iOS — otherwise the button
        // can land under the camera notch in landscape or under the home
        // indicator if the orientation flips.
        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.setAttribute('aria-label', 'Close ad');
        closeBtn.textContent = '×';
        closeBtn.style.cssText = `
      position: absolute;
      top: max(16px, env(safe-area-inset-top, 0px));
      right: max(16px, env(safe-area-inset-right, 0px));
      width: 40px;
      height: 40px;
      border: 0;
      border-radius: 50%;
      background: rgba(0, 0, 0, 0.6);
      color: #fff;
      font-size: 24px;
      line-height: 40px;
      padding: 0;
      cursor: pointer;
      z-index: 1000002;
      display: none;
    `;
        closeBtn.addEventListener('click', onCloseClick);
        this.closeBtn = closeBtn;
        this.overlay.appendChild(slotDiv);
        this.overlay.appendChild(progressBar);
        this.overlay.appendChild(closeBtn);
        document.body.appendChild(this.overlay);
        // Fit-to-viewport: scale the slot down if the creative is larger
        // than the viewport. The handler also runs once at mount so an
        // already-small phone viewport gets the scale applied before the
        // creative paints. We listen to `resize` + `orientationchange`
        // (some old iOS versions emit only orientationchange) and also to
        // `visualViewport.resize` (iOS URL-bar show/hide doesn't fire a
        // `window.resize` in every WebKit version).
        fitSlotToViewport(w, h);
        const handler = () => fitSlotToViewport(w, h);
        window.addEventListener('resize', handler);
        window.addEventListener('orientationchange', handler);
        if (typeof window.visualViewport !== 'undefined' && window.visualViewport) {
            window.visualViewport.addEventListener('resize', handler);
        }
        this.viewportResizeHandler = handler;
        if (closeDelayMs <= 0) {
            // Opt-out path: no countdown, X tappable immediately.
            this.revealCloseButton();
            return;
        }
        // Kick off the depletion on the next frame so the browser commits the
        // initial `scaleX(1)` before the transition starts. Without the rAF,
        // some browsers collapse the start+end styles and the bar jumps
        // straight to empty without animating.
        progressBar.style.transition = `transform ${closeDelayMs}ms linear`;
        if (typeof requestAnimationFrame === 'function') {
            requestAnimationFrame(() => {
                // Guarded against teardown racing the rAF callback.
                if (this.progressBar === progressBar) {
                    progressBar.style.transform = 'scaleX(0)';
                }
            });
        }
        else {
            progressBar.style.transform = 'scaleX(0)';
        }
        this.closeDelayTimer = setTimeout(() => {
            this.closeDelayTimer = null;
            this.revealCloseButton();
        }, closeDelayMs);
    }
    /**
     * Hide the countdown bar and reveal the close X. Idempotent so the
     * failure path (which calls it to un-trap the user) and the natural
     * timer-completion path can both invoke it safely.
     */
    revealCloseButton() {
        if (this.closeDelayTimer !== null) {
            clearTimeout(this.closeDelayTimer);
            this.closeDelayTimer = null;
        }
        if (this.progressBar) {
            this.progressBar.style.display = 'none';
        }
        if (this.closeBtn) {
            this.closeBtn.style.display = 'block';
        }
    }
}
// ---- pure helpers (exported for unit tests) -------------------------------
/**
 * Resolve the interstitial slot size from the bid targeting.
 *
 * Preference order:
 *  1. `targeting.hb_size` — the AY winning bid's size (e.g. "300x250").
 *  2. `DEFAULT_INTERSTITIAL_SIZE` (300×250) — used when no AY bid won.
 *
 * `event.size` from `slotRenderEnded` is intentionally NOT consulted —
 * for AY's Prebid Universal Creative, GAM reports the creative's actual
 * 1×1 size and trusting it would perpetuate the rendered-iframe-stuck-
 * at-1×1 bug. Same reasoning as the banner force-resize.
 */
/**
 * Resolve the forced-view close delay from the config override, falling
 * back to `DEFAULT_CLOSE_DELAY_MS`. A non-finite or negative value is
 * coerced to `0` (immediately closeable) — the spec deliberately allows
 * `0` as the opt-out value so test harnesses can skip the wait, and we
 * don't want a bogus `NaN` from a partner to lock the overlay forever.
 */
export function resolveCloseDelayMs(override) {
    if (override === undefined)
        return DEFAULT_CLOSE_DELAY_MS;
    if (!Number.isFinite(override) || override < 0)
        return 0;
    return override;
}
/**
 * Compute the uniform scale factor needed so a `creativeW × creativeH`
 * creative fits inside `viewportW × viewportH`. Returns `1` if the
 * creative already fits (no upscaling — small ads stay at their
 * intrinsic size, otherwise a 300×250 on a tablet would look blurry).
 *
 * Exported for unit testing. The renderer reads `window.innerWidth /
 * innerHeight` at the call site so we don't have to mock the DOM here.
 */
export function computeSlotFitScale(creativeW, creativeH, viewportW, viewportH) {
    if (creativeW <= 0 || creativeH <= 0)
        return 1;
    if (viewportW <= 0 || viewportH <= 0)
        return 1;
    return Math.min(1, viewportW / creativeW, viewportH / creativeH);
}
/**
 * Apply `transform: scale(...)` to the interstitial slot div so the
 * creative fits within the current viewport. Touches the live DOM; the
 * pure math lives in `computeSlotFitScale` for testability.
 *
 * Called on mount and on every viewport resize / orientation change —
 * needs to be safe to call when the slot has been torn down (no-op if
 * the element is gone), since a late `resize` event after teardown
 * isn't impossible.
 */
export function fitSlotToViewport(creativeW, creativeH) {
    if (typeof document === 'undefined' || typeof window === 'undefined')
        return;
    const slotDiv = document.getElementById(SLOT_DIV_ID);
    if (!slotDiv)
        return;
    const scale = computeSlotFitScale(creativeW, creativeH, window.innerWidth || 0, window.innerHeight || 0);
    // Empty string clears the transform — important so a portrait→landscape
    // rotation that newly fits doesn't keep an old scale value.
    slotDiv.style.transform = scale < 1 ? `scale(${scale})` : '';
}
export function resolveInterstitialSize(targeting) {
    const hb = targeting['hb_size'];
    if (hb) {
        const m = /^(\d+)x(\d+)$/.exec(hb);
        if (m) {
            const w = parseInt(m[1], 10);
            const h = parseInt(m[2], 10);
            if (w > 1 && h > 1)
                return [w, h];
        }
    }
    return [DEFAULT_INTERSTITIAL_SIZE[0], DEFAULT_INTERSTITIAL_SIZE[1]];
}
/**
 * Force the rendered GPT iframe + its `__container__` wrapper to the
 * resolved size. GAM/PUC serves a 1×1 creative whose SafeFrame self-
 * resize doesn't propagate to the iframe element; without this, the
 * interstitial slot renders invisibly at 1×1. Mirrors
 * `defaultResizeFilledContainer` in gpt-banner.ts.
 *
 * Exported so the surface is unit-testable, but the implementation
 * touches the live DOM directly (no need for injection — it's only
 * called from within a `cmd.push` callback after GPT has populated the
 * slot).
 */
export function forceResizeInterstitial(w, h) {
    if (typeof document === 'undefined')
        return;
    const slotDiv = document.getElementById(SLOT_DIV_ID);
    if (!slotDiv)
        return;
    slotDiv.style.width = `${w}px`;
    slotDiv.style.height = `${h}px`;
    const inner = slotDiv.querySelector('div[id$="__container__"]');
    if (inner) {
        inner.style.width = `${w}px`;
        inner.style.height = `${h}px`;
        inner.style.minWidth = `${w}px`;
        inner.style.minHeight = `${h}px`;
    }
    const iframe = (inner !== null && inner !== void 0 ? inner : slotDiv).querySelector('iframe');
    if (iframe) {
        iframe.style.width = `${w}px`;
        iframe.style.height = `${h}px`;
        iframe.style.minWidth = `${w}px`;
        iframe.style.minHeight = `${h}px`;
        iframe.setAttribute('width', String(w));
        iframe.setAttribute('height', String(h));
    }
}
